import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { useMediaQuery } from '@chakra-ui/react';
import { documentAction, taskAction } from 'actions';
import { Skeleton } from 'components/ds';
import configs from 'configs';
import { HTTP_CODE_RESPONSE, TEMPORARY } from 'helpers/constant';
import { addTokenURL, getFileExtension } from 'helpers/file';
import { resolveDefaultModelId } from 'helpers/lib';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { UploadService } from 'services/upload/upload.service';
import { Task, TaskFileDocument } from 'types/reducers/task';
import { getStorageReadBlobToken } from 'utils/blob';
import { v4 as uuidv4 } from 'uuid';

import Chat from './Chat';
import FilePreviewDialog, { PreviewFileState } from './FilePreviewDialog';
import SelectFiles from './SelectFiles';
import SelectFilesMobile from './SelectFilesMobile';
import TasksList from './TasksList';
import TasksListMobile from './TasksListMobile';

const Summary = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { tasks, processingStreamingChunkStatusTask } = useSelector((state) => state.task);
  const models = useSelector((state: any) => state.model?.models || []);
  const [toggleSidebarMobile, setToggleSidebarMobile] = useState(true);

  const [isLoading, setIsLoading] = useState(false);
  const [isShowingPreviewFile, setIsShowingPreviewFile] = useState(false);
  const [previewFile, setPreviewFile] = useState<PreviewFileState>({});
  const { isOpen, onOpen: onOpenSidebarMobile, onClose: onCloseSidebarMobile } = useDisclosure();
  const [isLarger460] = useMediaQuery('(min-width: 30em)');

  const loadData = () => {
    setIsLoading(true);
    dispatch(taskAction.getTasks({ isKeepCurrentState: true }));
    setIsLoading(false);
  };

  const onChangeTask = (item: Task, resetModelTo?: string) => {
    if (processingStreamingChunkStatusTask) return;
    dispatch(
      taskAction.updateCurrentTask({
        task_id: item?.id,
        type: item?.type,
        selectedModelId: resetModelTo,
      })
    );
  };

  const newTask = useMemo(
    () => ({
      id: uuidv4(),
      type: TEMPORARY,
      name: t('common.addNew'),
      last_updated: new Date().toISOString(),
    }),
    [t]
  );

  const addNewTask = () => {
    if (processingStreamingChunkStatusTask) return;

    const defaultModelId = resolveDefaultModelId(models);

    const temporaryTask = tasks.data.find((task) => task.type === TEMPORARY);
    if (temporaryTask) {
      dispatch(
        taskAction.updateCurrentTask({
          task_id: temporaryTask.id,
          type: temporaryTask?.type,
          selectedModelId: defaultModelId,
        })
      );
      return;
    }

    dispatch(taskAction.addNewTask(newTask));
    dispatch(taskAction.clearTemporaryTask());
    dispatch(taskAction.updateTemporaryTaskId(newTask.id));
    onChangeTask(newTask, defaultModelId);
  };

  const checkReadTokenValid = (token: string | null) => {
    if (!token) {
      return false;
    }
    const searchParams = new URLSearchParams(token);
    const expiredTime = new Date(searchParams.get('se') as string).getTime();
    const currentTime = new Date().getTime();
    const offsetRefreshTime = configs.setting.TIMEOUT_REFRESH_SAS_TOKEN_IN_MS;

    return expiredTime > currentTime + offsetRefreshTime;
  };

  const onOpenFile = (file: TaskFileDocument) => {
    readFile(file);
  };

  const readFile = (file: TaskFileDocument) => {
    if (!checkReadTokenValid(getStorageReadBlobToken())) {
      dispatch(documentAction.getReadToken());
    }
    fetchData(file);
  };

  const fetchData = (file: TaskFileDocument) => {
    const fileUrl = addTokenURL(file?.url, getStorageReadBlobToken());
    setPreviewFile({
      url: fileUrl,
      name: file?.name,
      type: getFileExtension(file?.url),
    });

    (async () => {
      try {
        const res = await UploadService.fetchHeadFile(fileUrl);
        if (
          res.statusText !== 'OK' &&
          (res.status === HTTP_CODE_RESPONSE.UNAUTHORIZED ||
            res.status === HTTP_CODE_RESPONSE.NO_SAS_TOKEN)
        ) {
          readFile(file);
        } else {
          setIsShowingPreviewFile(true);
        }
      } catch (error) {
        console.log(error);
      }
    })();
  };

  const onClose = () => {
    setIsShowingPreviewFile(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    onCloseSidebarMobile();
  }, [isLarger460]);

  useEffect(() => {
    const handleOpenSidebarMobile = () => {
      onOpenSidebarMobile();
    };

    window.addEventListener('open-sidebar-mobile', handleOpenSidebarMobile);

    return () => {
      window.removeEventListener('open-sidebar-mobile', handleOpenSidebarMobile);
    };
  }, [onOpenSidebarMobile]);

  return (
    <Box overflow='hidden' h='100%' minH={0} bg='bg.canvas'>
      <FilePreviewDialog isOpen={isShowingPreviewFile} file={previewFile} onClose={onClose} />
      {isLoading ? (
        /* DS loading skeleton — mirrors 3-pane layout (design-guidelines.md §4) */
        <Flex gap={4} w='100%' h='100%' minH={0} p={2}>
          <Box flexShrink={0} display={{ base: 'none', xl: 'block' }} w='320px'>
            <Skeleton h='40px' borderRadius='md' mb={4} />
            <Skeleton h='60px' borderRadius='md' mb={2} />
            <Skeleton h='60px' borderRadius='md' mb={2} />
            <Skeleton h='60px' borderRadius='md' />
          </Box>
          <Box flexShrink={0} display={{ base: 'none', xl: 'block' }} w='360px'>
            <Skeleton h='40px' borderRadius='md' mb={4} />
            <Skeleton h='64px' borderRadius='md' mb={2} />
            <Skeleton h='64px' borderRadius='md' />
          </Box>
          <Box flex={1}>
            <Skeleton h='72px' borderRadius='lg' mb={4} maxW='560px' />
            <Skeleton h='120px' borderRadius='lg' maxW='680px' />
          </Box>
        </Flex>
      ) : (
        <>
          {isLarger460 && (
            <Flex
              justify='center'
              flex={1}
              gap={4}
              display={{ base: 'none', xl: 'flex' }}
              overflow='hidden'
              h='100%'
              minH={0}
            >
              <TasksList onAddNew={addNewTask} onChange={onChangeTask} />
              <SelectFiles onOpenFile={onOpenFile} />
              <Chat onAddNew={addNewTask} />
            </Flex>
          )}

          <Flex
            justify={'center'}
            display={{ base: 'flex', xl: 'none' }}
            overflow='hidden'
            h='100%'
            minH={0}
          >
            <Chat onAddNew={addNewTask} />
            <Drawer isOpen={isOpen} onClose={onCloseSidebarMobile} placement='left'>
              <DrawerOverlay bg='blackAlpha.500' backdropFilter='blur(3px)' />
              <DrawerContent h='100dvh' bg='bg.sidebar'>
                <DrawerCloseButton zIndex={1} color='text.muted' />
                <DrawerHeader
                  gap={2}
                  display='flex'
                  // Leave room for the close (X) button top-right so the Task List / Select files
                  // toggles don't render under it. (#summary-drawer-close-covered)
                  pr='48px'
                  borderColor='border.default'
                  borderBottomWidth='1px'
                >
                  <Button
                    flex={1}
                    onClick={() => setToggleSidebarMobile(true)}
                    size='ds-sm'
                    variant={toggleSidebarMobile ? 'ds-primary' : 'ds-outline'}
                  >
                    <Text isTruncated>{t('summary.taskList')}</Text>
                  </Button>
                  <Button
                    flex={1}
                    onClick={() => setToggleSidebarMobile(false)}
                    size='ds-sm'
                    variant={!toggleSidebarMobile ? 'ds-primary' : 'ds-outline'}
                  >
                    <Text isTruncated>{t('summary.selectFile')}</Text>
                  </Button>
                </DrawerHeader>
                <DrawerBody px={2} py={3}>
                  <Box display={`${toggleSidebarMobile ? 'block' : 'none'}`}>
                    <TasksListMobile
                      onAddNew={addNewTask}
                      onChange={onChangeTask}
                      onCloseSidebarMobile={onCloseSidebarMobile}
                    />
                  </Box>
                  <Box display={`${toggleSidebarMobile ? 'none' : 'block'}`}>
                    <SelectFilesMobile onOpenFile={onOpenFile} />
                  </Box>
                </DrawerBody>
              </DrawerContent>
            </Drawer>
          </Flex>
        </>
      )}
    </Box>
  );
};

export default Summary;
